import { getIcon } from '@/common/lib/getIcon';
import {
  type ArenaPackRewardDto,
  type BoxReward,
  type RewardItemObjectDto,
} from '@/common/types/shop/marketBoxes';

import dicePng from '/img/icons/dice.png';
import diceWebp from '/img/icons/dice.webp';

// Union type for the reward parameter
type Reward = BoxReward | ArenaPackRewardDto;

function isBoxRewardArray<K extends string>(
  arr: Array<Record<K, number>> | Array<Record<K, RewardItemObjectDto>>,
): arr is Array<Record<K, number>> {
  const first = arr[0];

  if (!first) return false;

  const value = Object.values(first)[0];

  return typeof value === 'number';
}

function rewardArray(reward: Reward): {
  label: string;
  value: number;
  icon: { png: string; webp?: string } | null;
  order?: number;
  fixed?: boolean;
}[] {
  const rewardItems: {
    label: string;
    value: number;
    icon: { png: string; webp?: string } | null;
    order?: number;
    fixed?: boolean;
  }[] = [];

  // Додаємо cash, якщо він не null
  if (reward.cash) {
    rewardItems.push({
      label: 'cash',
      value: reward.cash,
      icon: {
        png: '/img/icons/cash-large.png',
        webp: '/img/icons/cash-large.webp',
      },
    });
  }

  // TGem (specific to ArenaPackRewardDto)
  if ('tgem' in reward && reward.tgem) {
    rewardItems.push({
      label: 'tgem',
      value: reward.tgem,
      icon: {
        png: '/img/icons/tcoin.png',
        webp: '/img/icons/tcoin.webp',
      },
    });
  }

  // Resources (handling different structures)
  if (reward.resources) {
    if (isBoxRewardArray(reward.resources)) {
      // BoxReward case: resources is Array<Record<ResourceTypeText, number>>
      rewardItems.push(
        ...reward.resources.flatMap((resource) =>
          Object.entries(resource).map(([label, value]) => ({
            label,
            value,
            icon: getIcon(label),
          })),
        ),
      );
    } else {
      // ArenaPackRewardDto case: resources is Array<Record<ResourceTypeText, RewardItemObjectDto>>
      rewardItems.push(
        ...reward.resources.flatMap((resource) =>
          Object.entries(resource).map(([label, { amount, order, fixed }]) => ({
            label,
            value: amount,
            icon: getIcon(label),
            order, // Store order for sorting
            ...(fixed ? { fixed } : {}),
          })),
        ),
      );
    }
  }

  // Materials (handling different structures)
  if (reward.materials) {
    if (isBoxRewardArray(reward.materials)) {
      // BoxReward case: materials is Array<Record<PlantTypeText, number>>
      rewardItems.push(
        ...reward.materials.flatMap((material) =>
          Object.entries(material).map(([label, value]) => ({
            label,
            value,
            icon: getIcon(label),
          })),
        ),
      );
    } else {
      // ArenaPackRewardDto case: materials is Array<Record<PlantTypeText, RewardItemObjectDto>>
      rewardItems.push(
        ...reward.materials.flatMap((material) =>
          Object.entries(material).map(([label, { amount, order, fixed }]) => ({
            label,
            value: amount,
            icon: getIcon(label),
            order,
            ...(fixed ? { fixed } : {}),
          })),
        ),
      );
    }
  }

  // Products (specific to ArenaPackRewardDto)
  if ('products' in reward && reward.products) {
    rewardItems.push(
      ...reward.products.flatMap((product) =>
        Object.entries(product).map(([label, { amount, order, fixed }]) => ({
          label,
          value: amount,
          icon: getIcon(label),
          order,
          ...(fixed ? { fixed } : {}),
        })),
      ),
    );
  }

  if (reward.dice) {
    rewardItems.push({
      label: 'dice',
      value: reward.dice,
      icon: {
        png: dicePng,
        webp: diceWebp,
      },
    });
  }

  let vipValue: number | undefined;

  if ('vip' in reward && reward.vip) {
    vipValue = reward.vip;
  } else if ('vipDays' in reward && reward.vipDays) {
    vipValue = reward.vipDays;
  }

  // А тепер додаємо, якщо значення було знайдено
  if (vipValue) {
    rewardItems.push({
      label: 'vip',
      value: vipValue,
      icon: {
        png: '/img/icons/vipCrown.png',
        webp: '/img/icons/vipCrown.webp',
      },
    });
  }

  return rewardItems.sort((a, b) => {
    // Якщо обидва елементи мають order, сортуємо за ним
    if (a.order !== undefined && b.order !== undefined) {
      return a.order - b.order;
    }

    // Якщо тільки a має order, він іде першим
    if (a.order !== undefined) {
      return -1;
    }

    // Якщо тільки b має order, він іде першим
    if (b.order !== undefined) {
      return 1;
    }

    // Якщо ні в кого немає order, зберігаємо початковий порядок
    return 0;
  });
}

export { rewardArray };
